import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import { QuestionSelectOption, QuestionLanguage, Image } from "../../../../types/admin";
import { v4 as uuidv4 } from "uuid";
import { ADMIN_LANG, AVAILABLE_LANGUAGES } from "../../../../settings";
import { changeQuestionModal } from "../../../../app/slices/modals/questionEditModal";
import { PlusCircleFill, Trash } from "react-bootstrap-icons";
import { Col, Dropdown, Form, Row, Accordion } from "react-bootstrap";
import MediaEditImage from "./MediaEditImage";

type Props = {
  language: string;
  is_draft: boolean;
  active_languages: string[];
  default_image: Image | null;
  languages: QuestionLanguage[];
  handleLanguageImageChange: (image: File | null, language: string) => void;
  handleDefaultImageChange: (image: File | null) => void;
};

const QuestionEditSelect = ({
  language,
  is_draft,
  active_languages,
  default_image,
  languages,
  handleLanguageImageChange,
  handleDefaultImageChange
}: Props) => {
  const { select_options, multiple, shuffle, layout } = useAppSelector((state) => state.questionEditModal);
  const dispatch = useAppDispatch();

  const handleAddOption = (type: string) => {
    if (!is_draft) return;
    const newOption: QuestionSelectOption = {
      option_id: uuidv4(),
      key: uuidv4(),
      type,
      points: 0,
      languages: AVAILABLE_LANGUAGES.map((lang) => ({
        language_code: lang,
        option: '',
      })),
    };
    dispatch(changeQuestionModal({ select_options: [...select_options, newOption] }));
  };

  const handleMultipleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(changeQuestionModal({ multiple: e.target.checked }));
  };

  const handleShuffleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(changeQuestionModal({ shuffle: e.target.checked }));
  }

  return (
    <Col xs={12}>
      <div className='pb-3'>
        <Accordion>
          <Accordion.Item eventKey="0">
            <Accordion.Header>{ADMIN_LANG.type_image}</Accordion.Header>
            <Accordion.Body>
              <MediaEditImage
                active_languages={active_languages}
                default_image={default_image}
                language={language}
                is_draft={is_draft}
                languages={languages}
                handleLanguageImageChange={handleLanguageImageChange}
                handleDefaultImageChange={handleDefaultImageChange}
                imageWidth='250px'
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
      <Form.Group controlId='multiple'>
        <Form.Switch
          className='form-label'
          checked={multiple}
          onChange={handleMultipleChange}
          disabled={!is_draft}
          label={ADMIN_LANG.multiple}
        />
      </Form.Group>
      <Form.Group controlId='shuffle'>
        <Form.Switch
          className='form-label'
          checked={shuffle}
          onChange={handleShuffleChange}
          label={ADMIN_LANG.shuffle_options}
        />
      </Form.Group>
      <div className='d-flex align-items-center pb-2 pt-3'>
        <h4 className='mb-0'>{ADMIN_LANG.select_options}</h4>
        {is_draft && (
          <Dropdown className='icon-dropdown ms-2'>
            <Dropdown.Toggle variant='icon'><PlusCircleFill size='1.8rem'
              title={ADMIN_LANG.add} /></Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => handleAddOption('basic')}><span>{ADMIN_LANG.select_basic}</span></Dropdown.Item>
              <Dropdown.Item
                onClick={() => handleAddOption('open')}><span>{ADMIN_LANG.select_open}</span></Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
      <Row className="gy-3">
        <Col xs={7}>
          <Form.Label className="mb-0">
            {`${ADMIN_LANG.title} (${language.toUpperCase()})`}
          </Form.Label>
        </Col>
        <Col xs={3}>
          <Form.Label className="mb-0">{ADMIN_LANG.points}</Form.Label>
        </Col>
        {select_options.map((option) => {
          return (
            <SelectOptionItem
              key={option.key}
              selectOption={option}
              is_draft={is_draft}
              language={language}
            />
          );
        })}
      </Row>
    </Col>
  );
};

export default QuestionEditSelect;

const SelectOptionItem = ({
  selectOption,
  language,
  is_draft,
}: {
  selectOption: QuestionSelectOption;
  language: string;
  is_draft: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { select_options } = useAppSelector((state) => state.questionEditModal);

  const handleRemoveOption = () => {
    if (!is_draft) return;
    dispatch(changeQuestionModal({
      select_options: select_options.filter((option) => option.option_id !== selectOption.option_id),
    }));
  };

  const handleOptionPointsChange = (points: number) => {
    const updatedOptions = select_options.map((option) =>
      option.option_id === selectOption.option_id ? { ...option, points } : option
    );
    dispatch(changeQuestionModal({ select_options: updatedOptions }));
  };

  const handleOptionTitleChange = (title: string) => {
    const updatedOptions = select_options.map((option) => {
      if (option.option_id === selectOption.option_id) {
        return {
          ...option,
          languages: option.languages.map((languageOption) =>
            languageOption.language_code === language
              ? { ...languageOption, option: title }
              : languageOption
          ),
        };
      }
      return option;
    });
    dispatch(changeQuestionModal({ select_options: updatedOptions }));
  };

  const languageOption = selectOption.languages.find(
    (lang) => lang.language_code === language
  );

  const optionHelpText = selectOption.languages
    .filter((lang) => lang.language_code !== language && lang.option)
    .map((lang) => (
      <div key={lang.language_code}>
        <strong>{lang.language_code.toUpperCase()}: </strong>
        {lang.option}
      </div>
    ));

  return (
    <Col xs={12} key={selectOption.key}>
      <div className="form-group row">
        <Col xs={7}>
          <Form.Group controlId={`option_${selectOption.key}_${language}`}>
            <Form.Control
              type="text"
              maxLength={255}
              value={languageOption?.option || ""}
              onChange={(e) => handleOptionTitleChange(e.target.value)}
            />
            {optionHelpText.length > 0 && (
              <Form.Text className='help-text'>
                {optionHelpText}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col xs={3}>
          <Form.Group controlId={`points_${selectOption.key}`}>
            {selectOption.type === 'open' ? (
              <Form.Text className='open-text'>
                {ADMIN_LANG.select_open}
              </Form.Text>
            ) : (
              <Form.Control
                type="number"
                value={selectOption.points}
                onChange={(e) =>
                  handleOptionPointsChange(parseInt(e.target.value))
                }
                disabled={!is_draft}
              />
            )}
          </Form.Group>
        </Col>
        <Col xs={2} className="d-flex justify-content-center align-items-center">
          <Trash
            onClick={handleRemoveOption}
            className={`utility-icon ${is_draft ? "red" : "disabled"}`}
          />
        </Col>
      </div>
    </Col>
  );
};